import { useState, type KeyboardEvent } from 'react';
import { Modal } from './Modal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { DateField } from './DateTimeField.js';
import { Dd } from './Dropdown.js';
import type { RiskConfig } from '@ehr/shared';
import { instrumentFor, scoreFromScoring } from '../lib/scoreHistory.js';

export interface LibField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  options: string[];
  /** #54 - Form Builder autopopulation key; drives demographics pre-fill on open. */
  dataKey?: string | null;
  /** #54 - receive-only: takes the shared value but never publishes its own. */
  prefillOverwrite?: boolean | null;
}
export interface LibForm {
  id: string;
  name: string;
  description: string;
  source: 'system' | 'custom';
  /** credifyfbs#240: a System Form's group in the Form Builder ('screener', 'template', 'clinical'). */
  category?: string;
  contactTypes?: string[];
  fields?: LibField[];
  owner?: string | null;
  /** ehr#429: the form's own scoring (a built-in's or the Form Builder's), when it has one. */
  scoring?: RiskConfig | null;
}
export type FormMode = 'view' | 'fill' | 'edit';

/**
 * #54 - Form Builder autopopulation: two fields are linked iff they carry the
 * same `dataKey`, and a value in one mirrors to every field sharing that key
 * (docs/AUTOPOPULATION.md §1, §7). This syncs a set of answers so all fields in
 * a key-group hold that group's value — used to seed the modal (so a demographic
 * prefill or a saved answer lands on every linked field) and after each edit.
 */
function syncKeyGroups(
  fields: LibField[],
  answers: Record<string, string>,
): Record<string, string> {
  const valueByKey: Record<string, string> = {};
  for (const f of fields) {
    if (!f.dataKey) continue;
    const v = answers[f.id];
    if (v != null && v !== '' && valueByKey[f.dataKey] == null) valueByKey[f.dataKey] = v;
  }
  if (Object.keys(valueByKey).length === 0) return answers;
  const out = { ...answers };
  for (const f of fields) {
    if (f.dataKey && valueByKey[f.dataKey] != null) out[f.id] = valueByKey[f.dataKey]!;
  }
  return out;
}

const MODE_TITLE: Record<FormMode, string> = {
  view: 'View',
  fill: 'Fill',
  edit: 'Edit Fields',
};

// #138 - group consecutive radio/scale fields that share the same options into a
// matrix (rows = items, columns = the scale), mirroring the Form Builder's
// preview. A preceding heading becomes the matrix stem. Fields that don't group
// render on their own.
export type Block =
  | { kind: 'matrix'; title?: string; options: string[]; items: LibField[] }
  | { kind: 'heading'; text: string }
  | { kind: 'field'; field: LibField };

const sameOpts = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

export function buildBlocks(fields: LibField[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;
  while (i < fields.length) {
    const f = fields[i]!;
    const isScale = (f.type === 'radio' || f.type === 'scale') && f.options.length > 0;
    if (isScale) {
      const opts = f.options;
      const items: LibField[] = [];
      while (i < fields.length) {
        const g = fields[i]!;
        if ((g.type === 'radio' || g.type === 'scale') && sameOpts(g.options, opts)) {
          items.push(g);
          i++;
        } else break;
      }
      // A run of 2+ same-scale items is a matrix; a lone item stays a field row.
      let title: string | undefined;
      const last = blocks[blocks.length - 1];
      if (items.length >= 2 && last && last.kind === 'heading') {
        title = last.text;
        blocks.pop();
      }
      if (items.length >= 2) blocks.push({ kind: 'matrix', title, options: opts, items });
      else for (const it of items) blocks.push({ kind: 'field', field: it });
      continue;
    }
    if (f.type === 'heading' || f.type === 'section') {
      blocks.push({ kind: 'heading', text: f.label });
      i++;
      continue;
    }
    blocks.push({ kind: 'field', field: f });
    i++;
  }
  return blocks;
}

/**
 * Score across scored scale matrices (frequency scales with 3+ options), mirroring the
 * Form Builder's scoring band (e.g. PHQ-9 → 0/27). Shared by the view and its print (ehr#253).
 */
export function blocksScore(
  blocks: Block[],
  answers: Record<string, string>,
): { score: number; scoreMax: number; hasScore: boolean } {
  let score = 0;
  let scoreMax = 0;
  let hasScore = false;
  for (const b of blocks) {
    if (b.kind === 'matrix' && b.options.length >= 3) {
      hasScore = true;
      for (const it of b.items) {
        scoreMax += b.options.length - 1;
        const idx = b.options.indexOf(answers[it.id] ?? '');
        if (idx > 0) score += idx;
      }
    }
  }
  return { score, scoreMax, hasScore };
}

/**
 * credifyfbs#203 - the required fields still blank in a fill, in form order. Headings and
 * sections carry no answer, so they never count.
 */
export function requiredBlank(fields: LibField[], answers: Record<string, string>): LibField[] {
  return fields.filter(
    (f) =>
      f.required &&
      f.type !== 'heading' &&
      f.type !== 'section' &&
      (isAckBox(f) ? !ackTicked(answers[f.id]) : (answers[f.id] ?? '').trim() === ''),
  );
}

/**
 * ehr#413 - a checkbox with no choices is one acknowledgment box ("I have received the Notice of
 * Privacy Practices"): ticked saves 'true', unticked ''. It was drawn as two boxes, Yes and No, and
 * ticking No counted as answering a required acknowledgment. 'Yes' (saved that way before) reads as ticked.
 */
export const isAckBox = (f: Pick<LibField, 'type' | 'options'>) =>
  f.type === 'checkbox' && !f.options.length;
export const ackTicked = (v: string | undefined) => v === 'true' || v === 'Yes';

/**
 * ehr#480 - a single-choice answer clicked again clears (Chase: "it should de-select it"); any
 * other choice picks that one.
 */
export const toggleChoice = (current: string, option: string) => (current === option ? '' : option);

/**
 * ehr#480 - a radio fires no change when it's already checked, so the chosen one clears itself
 * from its click, or from Space (kept from re-checking it). Arrow keys still move the pick.
 */
const clearWhenChosen = (chosen: boolean, clear: () => void) => ({
  onClick: () => {
    if (chosen) clear();
  },
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
    if (chosen && e.key === ' ') {
      e.preventDefault();
      clear();
    }
  },
});

/**
 * In-app form popup — renders a Form Builder form inside the EHR (no jump to
 * forms.credifyfast.com). `view` = read-only, `fill` = interactive inputs,
 * `edit` = field configuration. Fill/edit are non-persisting in this test pass:
 * submitting or editing does not yet write back to the Form Builder.
 */
export function FormModal({
  form,
  mode,
  onClose,
  onSave,
  initialAnswers,
  saving,
}: {
  form: LibForm;
  mode: FormMode;
  onClose: () => void;
  // P2: when provided, Fill persists the answers instead of a no-op test submit.
  // credifyfbs#203: draft = saved unfinished (ehr#480 Save as draft, or required fields blank).
  onSave?: (answers: Record<string, string>, opts: { draft: boolean }) => void;
  initialAnswers?: Record<string, string>;
  saving?: boolean;
}) {
  const fields = form.fields ?? [];
  // #54 - seed with linked fields already in sync (a demographic prefill or saved
  // answer on one field of a key-group lands on all of them).
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    syncKeyGroups(fields, initialAnswers ?? {}),
  );
  const [submitted, setSubmitted] = useState(false);
  // credifyfbs#203 (Chase 10-13): Save with required fields blank asks first (Go back / Save as
  // draft / Don't save changes). Go back marks the blank ones until they're filled.
  const [askBlank, setAskBlank] = useState(false);
  const [showMissing, setShowMissing] = useState(false);

  // #54 - write a field's value and mirror it to every field sharing its dataKey,
  // matching the Form Builder runtime. A receive-only field (prefillOverwrite)
  // takes values but never publishes its own, so don't propagate from it.
  const setFieldValue = (field: LibField, v: string) => {
    setAnswers((a) => {
      const next = { ...a, [field.id]: v };
      if (field.dataKey && !field.prefillOverwrite) {
        for (const sib of fields) {
          if (sib.id !== field.id && sib.dataKey === field.dataKey) next[sib.id] = v;
        }
      }
      return next;
    });
  };

  const title = `${MODE_TITLE[mode]} · ${form.name}`;

  // #135 - system screeners now carry their items (mirrored from the Form
  // Builder's INSTRUMENTS list), so they render through the same field path as
  // custom forms below. The fields.length === 0 fallback still covers any form
  // that arrives without a schema.
  if (fields.length === 0) {
    return (
      <Modal
        title={title}
        onClose={onClose}
        actions={
          <button className="btn" onClick={onClose}>
            Close
          </button>
        }
      >
        <p className="muted">This form has no fields to display.</p>
      </Modal>
    );
  }

  const blocks = buildBlocks(fields);
  const readOnly = mode === 'view';

  const byPosition = blocksScore(blocks, answers);
  // ehr#429: a form with its own scoring counts every scored question at its real points (AUDIT
  // 9-10 score 0 / 2 / 4; Yes / No screeners such as DAST-10 and C-SSRS score too)
  const scored = form.scoring?.sections.length ? scoreFromScoring(form.scoring, answers) : null;
  const score = scored ? scored.score : byPosition.score;
  const scoreMax = scored ? scored.max : byPosition.scoreMax;
  const hasScore = form.scoring?.sections.length ? scored !== null : byPosition.hasScore;
  // ehr#412: the severity band beside the score (PHQ-9 "Moderate", GAD-7 "Mild"…), as the Score
  // history shows it. Only for a known instrument whose item count matches, and once answered.
  const scoredCount = blocks.reduce(
    (n, b) => n + (b.kind === 'matrix' && b.options.length >= 3 ? b.items.length : 0),
    0,
  );
  const answeredAny = blocks.some(
    (b) => b.kind === 'matrix' && b.options.length >= 3 && b.items.some((it) => answers[it.id]),
  );
  const band = scored
    ? scored.band
    : answeredAny
      ? (instrumentFor(form.name, scoredCount)?.bands.find(
          (x) => score >= x.min && score <= x.max,
        ) ?? null)
      : null;
  const blank = mode === 'fill' ? requiredBlank(fields, answers) : [];
  const missing = new Set(showMissing ? blank.map((f) => f.id) : []);
  const save = () => {
    if (!onSave) return setSubmitted(true);
    if (blank.length) return setAskBlank(true);
    onSave(answers, { draft: false });
  };
  const goBack = () => {
    setAskBlank(false);
    setShowMissing(true);
    // After the marks render: bring the first blank field into view.
    setTimeout(() => {
      document
        .querySelector('.fm-full [data-fm-missing]')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  };

  const actions =
    mode === 'fill' ? (
      <>
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        {/* ehr#480: a clinician can save a draft any time, not only with required fields blank. */}
        {onSave && (
          <button
            className="btn"
            onClick={() => onSave(answers, { draft: true })}
            disabled={saving}
            title="Save what's filled in so far and finish it later"
          >
            Save as draft
          </button>
        )}
        <button className="btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : onSave ? 'Save form' : 'Submit (test)'}
        </button>
      </>
    ) : (
      <button className="btn" onClick={onClose}>
        Close
      </button>
    );

  return (
    <Modal
      title={title}
      onClose={onClose}
      actions={actions}
      // ehr#363 (Jay 10-11): full screen, so a long form scrolls with the bar on the right.
      className={readOnly ? 'fm-wide fm-full fm-view' : 'fm-wide fm-full'}
    >
      {form.description && <p className="muted">{form.description}</p>}
      {askBlank && onSave && (
        <ConfirmDialog
          message={
            <>
              <p>
                <strong>
                  {blank.length === 1
                    ? '1 required field is blank:'
                    : `${blank.length} required fields are blank:`}
                </strong>
              </p>
              {/* Labels often end in "?", so they're listed, not run into a sentence. */}
              <ul>
                {blank.slice(0, 5).map((f) => (
                  <li key={f.id}>{f.label || '(untitled)'}</li>
                ))}
                {blank.length > 5 && <li>and {blank.length - 5} more</li>}
              </ul>
              <p>
                Go back to fill them in, save it as a draft to finish later, or close without
                saving.
              </p>
            </>
          }
          cancelLabel="Go back"
          altLabel="Don't save changes"
          onAlt={() => {
            setAskBlank(false);
            onClose();
          }}
          confirmLabel="Save as draft"
          danger={false}
          busy={saving}
          onConfirm={() => {
            setAskBlank(false);
            onSave(answers, { draft: true });
          }}
          onClose={goBack}
        />
      )}
      {showMissing && blank.length > 0 && (
        <p className="demo-val-err" role="alert">
          {blank.length === 1
            ? '1 required field is still blank.'
            : `${blank.length} required fields are still blank.`}
        </p>
      )}
      {submitted && (
        <p className="muted">
          <span className="badge normal">captured</span> Test submit — not saved to the Form
          Builder.
        </p>
      )}
      <div className="fm-body">
        {blocks.map((b, i) =>
          b.kind === 'matrix' ? (
            <div className="fm-matrix-block" key={i}>
              {b.title && (
                <div className="fm-matrix-stem">
                  {b.title}
                  {b.items.some((it) => it.required) ? <span className="fm-req"> *</span> : null}
                  {b.items.some((it) => missing.has(it.id)) && (
                    <span className="fm-missing-msg"> Answer every row</span>
                  )}
                </div>
              )}
              <div className="fm-matrix-wrap">
                <table className="fm-matrix">
                  <thead>
                    <tr>
                      <th />
                      {b.options.map((o) => (
                        <th key={o}>{o}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.items.map((it) => (
                      <tr
                        key={it.id}
                        className={missing.has(it.id) ? 'fm-row-missing' : undefined}
                        data-fm-missing={missing.has(it.id) ? '' : undefined}
                      >
                        <td className="fm-matrix-item">{it.label}</td>
                        {b.options.map((o) => (
                          <td className="fm-matrix-cell" key={o}>
                            <input
                              type="radio"
                              name={it.id}
                              checked={(answers[it.id] ?? '') === o}
                              disabled={readOnly}
                              onChange={() => setFieldValue(it, o)}
                              // ehr#480: click (or Space on) the chosen answer again to clear it
                              {...clearWhenChosen((answers[it.id] ?? '') === o, () =>
                                setFieldValue(it, ''),
                              )}
                              aria-label={`${it.label}: ${o}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : b.kind === 'heading' ? (
            <div className="form-section-head" key={i}>
              {b.text}
            </div>
          ) : (
            <FieldRow
              key={b.field.id}
              field={b.field}
              mode={mode}
              value={answers[b.field.id] ?? ''}
              onChange={(v) => setFieldValue(b.field, v)}
              invalid={missing.has(b.field.id)}
            />
          ),
        )}
        {hasScore && (
          <div className="fm-score-wrap">
            <div className="fm-score-head">Score</div>
            <div className="fm-score">
              <span className="fm-score-label">{form.name} (Scoring)</span>
              <span className="fm-score-val">
                <b>{score}</b> / {scoreMax}
                {band && (
                  <span className={`sh-band sev-${band.severity} fm-score-band`}>{band.label}</span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function FieldRow({
  field,
  mode,
  value,
  onChange,
  invalid = false,
}: {
  field: LibField;
  mode: FormMode;
  value: string;
  onChange: (v: string) => void;
  /** credifyfbs#203: required and still blank, after Go back. */
  invalid?: boolean;
}) {
  // #58 - a section heading is display-only: render it once as a heading, not as
  // a labeled field row (which showed the text twice — the muted label plus the
  // <strong> control below it).
  if (field.type === 'heading' || field.type === 'section') {
    return <div className="form-section-head">{field.label}</div>;
  }

  const label = (
    <span className="muted">
      {field.label || '(untitled)'} {field.required ? '*' : ''}
    </span>
  );

  // Edit mode: show the field's configuration rather than an input.
  if (mode === 'edit') {
    return (
      <label className="demo-field">
        {label}
        <span>
          <span className="badge info">{field.type}</span>
          {field.required ? ' · required' : ''}
          {field.options.length ? ` · options: ${field.options.join(', ')}` : ''}
        </span>
      </label>
    );
  }

  const readOnly = mode === 'view';
  const control = renderControl(field, value, onChange, readOnly);
  // ehr#480: a set of choices sits in a div, not a label: a click on the question text would
  // otherwise click its first choice, and now that clears it when it's the one picked.
  const Wrap =
    field.type === 'radio' || (field.type === 'checkbox' && !isAckBox(field)) ? 'div' : 'label';
  return (
    <Wrap
      className={invalid ? 'demo-field demo-field-invalid' : 'demo-field'}
      data-fm-missing={invalid ? '' : undefined}
    >
      {label}
      {control}
      {invalid && <span className="fm-missing-msg">This field is required</span>}
    </Wrap>
  );
}

function renderControl(
  field: LibField,
  value: string,
  onChange: (v: string) => void,
  readOnly: boolean,
) {
  const disabled = readOnly;
  switch (field.type) {
    case 'textarea':
    case 'paragraph':
      return (
        <textarea
          rows={3}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'select':
      return (
        <Dd
          value={value}
          disabled={disabled}
          onChange={onChange}
          ariaLabel={field.label}
          placeholder="—"
          options={field.options.map((o) => ({ value: o, label: o }))}
        />
      );
    case 'checkbox':
      if (isAckBox(field))
        return (
          <div className="form-optionset" role="group">
            <label className="form-option">
              <input
                type="checkbox"
                name={field.id}
                disabled={disabled}
                checked={ackTicked(value)}
                onChange={(e) => onChange(e.target.checked ? 'true' : '')}
              />
              <span>Yes</span>
            </label>
          </div>
        );
    // falls through: a checkbox with choices is laid out like radio buttons
    case 'radio':
      // #58 - lay options out inline (wrapping) like the Form Builder, and give
      // the inputs a class so the modal's `input { width:100% }` can't stretch
      // the circle and shove the label to the right.
      return (
        <div
          className="form-optionset"
          role={field.type === 'radio' ? 'radiogroup' : 'group'}
          aria-label={field.label || undefined}
        >
          {(field.options.length ? field.options : ['Yes', 'No']).map((o) => (
            <label key={o} className="form-option">
              <input
                type={field.type === 'checkbox' ? 'checkbox' : 'radio'}
                name={field.id}
                disabled={disabled}
                checked={value === o}
                // ehr#480: the chosen answer clicked again clears. A checkbox toggles through its
                // change; a radio fires none when already checked, so its click clears it.
                onChange={() => onChange(field.type === 'checkbox' ? toggleChoice(value, o) : o)}
                {...(field.type === 'checkbox'
                  ? {}
                  : clearWhenChosen(value === o, () => onChange('')))}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    case 'number':
      return (
        <input
          type="number"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'date':
      return <DateField value={value} onChange={onChange} disabled={disabled} />;
    default:
      return (
        <input
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
